import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { Graph, UIState } from './StateMachine';
import { useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

export abstract class Director {
  abstract name: string;
  abstract state: { [key: string]: UIState };
  abstract data: { [key: string]: BehaviorSubject<any> };
  abstract event: { [key: string]: Subject<any> };

  private _initState: UIState = new UIState('init');
  private _state: BehaviorSubject<UIState> = new BehaviorSubject(this._initState);
  private subList: Subscription[] = [];
  private callStack: string[] = [];
  protected router?: ReturnType<typeof useRouter>;
  protected searchParams?: ReturnType<typeof useSearchParams>;

  abstract graph(): UIState;

  init() {
    this._initState = this.graph();
    this._state = new BehaviorSubject(this._initState);

    this.reset();
  }

  reset() {
    this._state.next(this._initState);
  }

  transition(name: string) {
    const nextTransition = this._state.value.getNext(name);
    if (nextTransition) {
      this._state.next(nextTransition.to);
    } else {
      console.trace('invalid transition :', this._state.value, name, nextTransition);
    }
  }
  getTestCase() {
    const graph = Graph.parse(this._initState);
    return graph.getTestCase();
  }

  getInitState(): UIState {
    return this._initState;
  }

  getCurrentState(): UIState {
    return this._state.value;
  }

  getStateSubject() {
    return this._state;
  }

  is(name: string): boolean {
    return this._state.value.name === name;
  }

  abstract register(): void;

  protected sub<T>(key: string, subject: Subject<T>, func: (val: T) => Promise<void>) {
    this.subList.push(
      subject.subscribe(async (value) => {
        if (this.callStack.indexOf(key) === -1) {
          this.log(key, value);
          this.callStack.push(key);
          await func(value);
          this.callStack.pop();
        } else {
          if (process.env.NEXT_PUBLIC_MODE === 'DEV') {
            console.log('block subscribe by callstack', key, this.callStack);
          }
        }
      }),
    );
  }

  private log(...value: any[]) {
    if (process.env.NEXT_PUBLIC_MODE === 'DEV') {
      console.log(`### ${this.name}:`, ...value);
    }
  }

  destroy() {
    this.subList.forEach((sub) => sub.unsubscribe());
  }

  toJSON() {
    const output: any = {};
    Object.keys(this.data).forEach((key) => {
      output[key] = this.data[key].value;
    });
    return output;
  }

  withRouter(router: ReturnType<typeof useRouter>) {
    this.router = router;
    return this;
  }
  withSearchParams(searchParams: ReturnType<typeof useSearchParams>) {
    this.searchParams = searchParams;
    return this;
  }
}

export function useDirector<T extends Director>(
  director: T,
  init?: (director: T) => void | (() => void),
) {
  const ref = useRef(director);
  const [_, render] = useState(0);
  useEffect(() => {
    ref.current.init();
    const destructor = init ? init(ref.current) : undefined;
    const sub = ref.current.getStateSubject().subscribe((st) => {
      render(new Date().getTime());
    });
    return () => {
      ref.current.destroy();
      destructor && destructor();
      sub.unsubscribe();
    };
  }, [ref.current]);
  return ref.current;
}
